import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import SalesLoader from "../../../components/loaders/SalesLoader";
import { useWholesaleRecentTransac } from "../hooks/useWholesaleRecentTransac";
import { Badge } from "../../ui/badge";
import { formatCurrency } from "../../../utils/formatCurrency";



type OrderStatus =
  | "Completed"
  | "Processing"
  | "Shipped"
  | "Cancelled"
  | "Refunded"
  | "Authorized"
  | "Paid"
  | "Replaced"
  | "Exchanged";




function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const statusStyles = {
    Completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    Processing: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    Shipped: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    Cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    Refunded: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300",
    Replaced: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300",
    Authorized: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
    Paid: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300",
    Exchanged: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  };

  return (
    <Badge className={statusStyles[status]} variant="outline">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}


export function WholesaleSalesDataTable() {
  const { data: recentTransactions, isLoading } = useWholesaleRecentTransac(40);

  const data = useMemo(() => {
    return (recentTransactions || []).map((tx, index) => ({
      id: tx.id || `no-id-${index}`,
      date: new Date(tx.date),
      productId: 0,
      orderNumber: tx.orderNumber || "-",
      client: tx.customer,
      status: tx.status,
      channel: tx.channel,
      quantity: tx.quantity,
      unitPrice: tx.quantity ? tx.total / tx.quantity : 0,
      totalPrice: tx.total,
      cogs: tx.cogs,
      profit: tx.total - (tx.cogs ?? 0),
    }));
  }, [recentTransactions]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");



 

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => formatDate(row.getValue("date")),
    },
    {
      accessorKey: "orderNumber",
      header: "Order #",
    },
    {
      accessorKey: "client",
      header: "Client",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <OrderStatusBadge status={row.getValue("status")} />,
    },
    {
      accessorKey: "channel",
      header: "Channel",
    },
    {
      accessorKey: "quantity",
      header: "Qty",
      cell: ({ row }) => row.getValue("quantity"),
    },
    {
      accessorKey: "cogs",
      header: "COGS",
      cell: ({ row }) => {
    const raw = row.getValue("cogs");

    if (raw === null || raw === undefined) {
      return <span className="text-muted-foreground italic">N/A</span>;
    }

    const amount =
      typeof raw === "number" ? raw : parseFloat(raw as string);

    return formatCurrency(amount);
  },
    },
    {
      accessorKey: "totalPrice",
      header: "Total",
      cell: ({ row }) => formatCurrency(row.getValue("totalPrice")),
    },
    {
      accessorKey: "profit",
      header: "Profit",
      cell: ({ row }) => formatCurrency(row.getValue("profit")),
    },
  ];

  // Get unique categories and channels for filters
  const categories = [
    "all",
    ...Array.from(new Set(data.map((item) => item.status))),
  ].sort();
  const channels = [
    "all",
    ...Array.from(new Set(data.map((item) => item.channel))),
  ].sort();

  // Apply category and channel filters
  const filteredData = useMemo(() => {
    let result = data;
    if (categoryFilter !== "all") {
      result = result.filter((item) => item.status === categoryFilter);
    }
    if (channelFilter !== "all") {
      result = result.filter((item) => item.channel === channelFilter);
    }
    return result;
  }, [data, categoryFilter, channelFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <>
      <SummaryHeader
        title="Sales Data"
      />
      <Card className="col-span-2">
        <CardHeader>
          <ExportMenu
            data={table.getFilteredRowModel().rows.map((row) => row.original)}
            filename="sales-data"
            buttonVariant="outline"
            buttonSize="sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
              <div className="flex-1">
                <Input
                  placeholder="Filter Clients..."
                  value={
                    (table
                      .getColumn("client")
                      ?.getFilterValue() as string) ?? ""
                  }
                  onChange={(event) =>
                    table
                      .getColumn("client")
                      ?.setFilterValue(event.target.value)
                  }
                  className="max-w-sm"
                />
              </div>
              <div>
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category === "all" ? "All Categories" : category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((channel) => (
                      <SelectItem key={channel} value={channel}>
                        {channel === "all" ? "All Channels" : channel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-md border">
              {isLoading ? (
                <SalesLoader variant="sales-data" />
              ) : (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} of{" "}
                {table.getCoreRowModel().rows.length} row(s) displayed.
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
